/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionUserConfirmation';

const ActionUserConfirmationManager: ActionManagerFn<
  'ActionUserConfirmation'
> = (platform: Platform): ActionManager<'ActionUserConfirmation'> => {
  return {
    ...baseActionManager(platform, 'ActionUserConfirmation'),
    isChildAllowed: (page, item, childType) =>
      ['ActionOnConfirm', 'ActionOnCancel'].includes(childType),
    defaultValue,
    props: {
      ...baseActionProps,
      title: {
        label: 'Confirmation Dialog Title',
        type: 'expression',
        returnType: async () => 'string',
        required: true,
      },
      message: {
        label: 'Confirmation Message',
        type: 'expression',
        returnType: async () => 'string',
        required: true,
      },
    },
    name: 'User Confirmation',
    getDisplayText: (item) => item.props.message.value,
    icon: { iconName: 'exclamation-triangle' },
    description: `**Overview**<br/>
The User Confirmation action is a critical safety component designed to interrupt an action workflow with a modal dialog, asking the user to verify their intent. It functions as a "checkpoint" for irreversible or high-consequence operations, ensuring that accidental clicks do not result in data loss or unintended submissions.<br/><br/>
This component introduces branching logic into the action chain. It stops execution and presents a standard dialog with "Yes/Confirm" and "No/Cancel" options. Depending on the user's choice, it routes the workflow into one of two specific child paths: ActionOnConfirm or ActionOnCancel.

**Key Features**<br/>
* **Branching Execution:** Splits the workflow based on user input. Actions placed under ActionOnConfirm run only if "Yes" is clicked; actions under ActionOnCancel run if "No" or "Close" is clicked.
* **Strict Child Architecture:** Explicitly restricted via isChildAllowed to only accept ActionOnConfirm and ActionOnCancel as children, enforcing a clear binary decision structure.
* **Dynamic Content:** Both title and message properties support expressions, allowing the dialog to be context-aware (e.g., "Are you sure you want to delete Order #123?").
* **Blocking Behavior:** The dialog is modal, meaning it prevents interaction with the rest of the application until a decision is made.

**Properties**<br/>
* **Confirmation Dialog Title (title):** Expression (String)
  * *Description:* The header text of the modal window.
  * *Example:* 'Delete Record?' or 'Submit Application'.
* **Confirmation Message (message):** Expression (String)
  * *Description:* The detailed body text explaining the consequence of the action.
  * *Example:* 'This action cannot be undone. Are you sure you want to proceed?'

**Use Cases**<br/>
* **Deletion Safety:** The standard "Are you sure?" prompt attached to any "Delete" button to prevent accidental data removal.
* **Critical Submissions:** Verifying intent before submitting a final, binding contract or payment ("Once submitted, you cannot edit this form.").
* **Navigation Warnings:** Interrupting a user who clicks "Cancel" on a form with unsaved changes ("You have unsaved changes. Do you really want to leave?").
* **Session Management:** Confirming a "Sign Out" action to ensure the user didn't click the button by mistake.`,
  };
};

export default ActionUserConfirmationManager;
